// The signed-in caller's gated feature keys (backend `features`/`feature_grants`
// tables). UI-only mirror — every gated endpoint re-checks server-side.
export const CAPABILITIES_API = {
  ME: '/api/me/capabilities',
} as const;

// Split billing (backend — org-scoped, entities keyed by their Ulid `uid`).
export const SPLIT_BILLING_API = {
  // All splits the caller may see, spanning orgs — scoped server-side by the split's
  // organisation (domain = all; tenant = their tenant's organisations; organisation =
  // their own), same model as the invoices/helpdesk lists. Paged + searched
  // server-side (`page`/`pageSize`/`search`).
  ALL_SPLITS: '/api/split-billing/splits',
  // The parties a split may name — WeFact debtors of the reimbursement administration
  // linked into the caller's scope (every debtor for a domain caller) — unpaged, since
  // the split dialog's picker needs the whole set. Gated by the `invoices` feature like
  // the clients list it mirrors: a domain user outside that allow-list gets a 403.
  PARTIES: '/api/split-billing/parties',
  // What only WeFact's `debtor/show` carries about one party (its house style), fetched
  // once the party is picked rather than for the whole set. Selectable parties only.
  PARTY: (clientId: string) => `/api/split-billing/parties/${encodeURIComponent(clientId)}`,
  // The reimbursement administration's house styles, for a share's override dropdown.
  // Same gate as the parties.
  HOUSE_STYLES: '/api/split-billing/house-styles',
  // The locations already covered by a split, unpaged and scoped the same way — the
  // split dialog refuses a taken location the moment it is picked.
  SPLIT_LOCATION_IDS: '/api/split-billing/splits/location-ids',
  // Every split whose cuts no longer add up to the live CPMS price of its location (or
  // whose location/tariff CPMS no longer has), for the accountant's checklist. Domain-only.
  CHECKS: '/api/split-billing/checks',
  SPLITS: (orgId: string) => `/api/orgs/${orgId}/split-billing/splits`,
  SPLIT: (orgId: string, splitUid: string) => `/api/orgs/${orgId}/split-billing/splits/${splitUid}`,
} as const;

// The Finance checklist's ticks (backend, domain-only): which of its live rows someone
// has checked, shared by every domain user. Tick is a POST of `{ check, rowKey }`;
// untick a DELETE with both in the query (a split row's key is no path segment).
export const CHECKLIST_API = {
  TICKS: '/api/checklist/ticks',
} as const;

// Fees (backend — NRG's own money per IAM tenant/organisation, two kinds). The service
// fee is our cut on the reimbursement side (four tariff components; the split dialog
// reads the per-target one to validate against the location price). The billing fee is
// what the customer pays us per charging point (one amount; no consumer yet). For both,
// `{targetId}` is a tenant *or* an organisation id: the per-target GET is org-scoped
// and resolves (own row, else the tenant's), the list/PUT/DELETE are domain-only.
export const FEES_API = {
  SERVICE_FEE: (targetId: string) => `/api/orgs/${targetId}/split-billing/service-fee`,
  // Every configured service fee, unpaged — drives the management table.
  ALL_SERVICE_FEES: '/api/split-billing/service-fees',
  BILLING_FEE: (targetId: string) => `/api/orgs/${targetId}/billing-fee`,
  ALL_BILLING_FEES: '/api/billing-fees',
  // How many charging points (EVSEs) each configured target has (CPMS, scoped by the
  // target id as rootId) — what the per-charging-point fee is multiplied by. Domain-only.
  BILLING_FEE_CHARGING_POINTS: '/api/billing-fees/charging-points',
  // Every CPMS location whose owners, charging points or billing fee do not look right
  // (an owner without a fee, no owner, several owners, a zero fee, no EVSEs), for the
  // accountant's checklist. Domain-only.
  BILLING_FEE_CHECKS: '/api/billing-fees/checks',
} as const;

// Invoices (backend — clients and invoices as reported by the external invoicing
// system (WeFact) and the clients' links to IAM tenants/organisations, keyed by the
// external client id). List/detail/download/create/update take a `?dataset=` param
// (billing | reimbursement) selecting which WeFact administration answers; the
// mapping endpoints are dataset-independent.
export const INVOICES_API = {
  LIST: '/api/invoices',
  DETAIL: (id: string) => `/api/invoices/${encodeURIComponent(id)}`,
  DOWNLOAD: (id: string) => `/api/invoices/${encodeURIComponent(id)}/download`,
  // The attachment is named by `?filename=`, not by a path segment — filenames are
  // free text from the invoicing system and belong in a query value.
  ATTACHMENT: (id: string) => `/api/invoices/${encodeURIComponent(id)}/attachment`,
  CLIENTS: '/api/invoices/clients',
  CLIENT: (id: string) => `/api/invoices/clients/${encodeURIComponent(id)}`,
  // The administration's house styles a domain user can bill a client in (domain-only).
  CLIENT_HOUSE_STYLES: '/api/invoices/clients/house-styles',
  // Add/remove one link (domain-only). There is no list endpoint on the frontend side:
  // a client's links arrive as `assignments` on the clients and invoices rows.
  CLIENT_MAPPING: (clientId: string) =>
    `/api/invoices/client-mappings/${encodeURIComponent(clientId)}`,
} as const;

// QR payment (backend — the API proxies this to the external QR app; the caller's own
// IAM organisation/tenant id is injected server-side as `client`, domain callers get
// no filter and see everything).
export const QR_PAYMENT_API = {
  DATA: '/api/qr/data',
} as const;

// ODA (backend — the API proxies this to the external NrgOda API; domain-level
// callers only, since ODA data is global and has no organisation concept).
export const ODA_API = {
  METERING_POINTS: '/api/oda/metering-points',
  METERING_POINT: (eanId: string) => `/api/oda/metering-points/${encodeURIComponent(eanId)}`,
  METERING_POINT_READINGS: (eanId: string) =>
    `/api/oda/metering-points/${encodeURIComponent(eanId)}/readings`,
  GROUPS: '/api/oda/groups',
  GROUP: (id: number) => `/api/oda/groups/${id}`,
  GROUP_READINGS: (id: number) => `/api/oda/groups/${id}/readings`,
} as const;

// Alerts (backend — org-scoped alert rules + their run history, keyed by their Ulid
// `uid`). The scoped list spans orgs; mutations are addressed per organisation.
export const ALERTS_API = {
  // All rules the caller may see, spanning orgs — scoped server-side by the rule's
  // organisation, same model as the split-billing/helpdesk lists. Paged + searched.
  ALL_RULES: '/api/alerts/rules',
  RULES: (orgId: string) => `/api/orgs/${orgId}/alerts/rules`,
  RULE: (orgId: string, ruleUid: string) => `/api/orgs/${orgId}/alerts/rules/${ruleUid}`,
  RULE_TOGGLE: (orgId: string, ruleUid: string) =>
    `/api/orgs/${orgId}/alerts/rules/${ruleUid}/toggle`,
  RULE_EXECUTIONS: (orgId: string, ruleUid: string) =>
    `/api/orgs/${orgId}/alerts/rules/${ruleUid}/executions`,
} as const;

// Helpdesk (backend — charge-point fault reports, org-scoped and keyed by their Ulid
// `uid`). The reference catalogue is shared across orgs, so it lives off the org path.
export const HELPDESK_API = {
  REFERENCE: '/api/helpdesk/reference',
  // All reports the caller may see, spanning orgs — scoped server-side by the
  // report's operator (domain = all; tenant/organisation = only reports whose
  // operator is within their IAM scope), same model as the invoices list.
  ALL_REPORTS: '/api/helpdesk/reports',
  REPORTS: (orgId: string) => `/api/orgs/${orgId}/helpdesk/reports`,
  REPORT: (orgId: string, reportUid: string) => `/api/orgs/${orgId}/helpdesk/reports/${reportUid}`,
  FORWARD: (orgId: string, reportUid: string) =>
    `/api/orgs/${orgId}/helpdesk/reports/${reportUid}/forward`,
  PARTNER_RESPONSE: (orgId: string, reportUid: string) =>
    `/api/orgs/${orgId}/helpdesk/reports/${reportUid}/partner-response`,
  // Closing / reopening is its own sub-resource, not the whole-report PUT — see
  // SetReportStatusDto for why.
  STATUS: (orgId: string, reportUid: string) =>
    `/api/orgs/${orgId}/helpdesk/reports/${reportUid}/status`,
  // The client's own notification contacts. Flat, no orgId: a contact is filed against
  // an IAM tenant *or* organisation (a tenant keeps one list for its whole network), and
  // the server derives which from the caller's scope.
  CONTACTS: '/api/helpdesk/contacts',
  CONTACT: (contactUid: string) => `/api/helpdesk/contacts/${contactUid}`,
} as const;
